import React from 'react';
import ReactDOM from 'react-dom';
import { Amplify } from 'aws-amplify';
import { BrowserRouter as Router } from 'react-router-dom';
import { Provider } from 'react-redux';
import { createStore, combineReducers, applyMiddleware, compose } from 'redux';
import thunk from 'redux-thunk';

import App from './App';
import config from './config';
import { unregister } from './registerServiceWorker';
import { I18nProvider } from './i18n';

import 'bootstrap/dist/css/bootstrap.min.css';

import './theme/applyTheme';
import { applyDisplayPrefs, getStoredPrefs, DisplayModeProvider } from './theme/displayMode';
import './index.css';
import './styles/theme.css';
import authReducer from './store/reducers/auth';

const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;
const rootReducer = combineReducers({
    auth: authReducer
});

const store = createStore(rootReducer, composeEnhancers(
    applyMiddleware(thunk)
));

applyDisplayPrefs(getStoredPrefs());

async function refreshToken() {
    // refresh the token here and get the new token info
	try {
		const cognitoUser = await Amplify.Auth.currentAuthenticatedUser();
		const currentSession = await Amplify.Auth.currentSession();
		cognitoUser.refreshSession(currentSession.refreshToken, (err, session) => {

		//   console.log("ID TOKEN " + idToken);
		//   console.log("REFRESH TOKEN" + refreshToken);
		//   console.log("ACCESS TOKEN " + accessToken);
		console.log('Refresh token')
		});
	  } catch (e) {
		console.log('Unable to refresh Token', e);
	  }

}

Amplify.configure({
	Auth: {
		mandatorySignIn: true,
		// USER_PASSWORD_AUTH avoids Cognito's SRP + dangling TOTP bug that
		// returns "Incorrect username or password" after MFA is skipped/disabled.
		authenticationFlowType: 'USER_PASSWORD_AUTH',
		region: config.cognito.REGION,
		userPoolId: config.cognito.USER_POOL_ID,
		identityPoolId: config.cognito.IDENTITY_POOL_ID,
		userPoolWebClientId: config.cognito.APP_CLIENT_ID,
		refreshHandlers: {
			'developer': refreshToken // the property could be 'google', 'facebook', 'amazon', 'developer', OpenId domain
		}
	},
	API: {
		endpoints: [
			{
				name: 'task_active',
				endpoint: config.apiGateway.URL + '/task_active',
				region: config.apiGateway.REGION,				
			},
			{
				name: 'task_active_all',
				endpoint: config.apiGateway.URL + '/task_active_all',
				region: config.apiGateway.REGION,				
			},
			{
				name: 'task_by_asset',
				endpoint: config.apiGateway.URL + '/task_by_asset',
				region: config.apiGateway.REGION,
			},
			{
				name: 'users',
				endpoint: config.apiGateway.URL + '/users',
				region: config.apiGateway.REGION,
			},		
			{
				name: 'user_disable',
				endpoint: config.apiGateway.URL + '/user_disable',
				region: config.apiGateway.REGION,
			},		
			{
				name: 'user_enable',
				endpoint: config.apiGateway.URL + '/user_enable',
				region: config.apiGateway.REGION,
			},			
			{
				name: 'user_confirm',
				endpoint: config.apiGateway.URL + '/user_confirm',
				region: config.apiGateway.REGION,
			},
			{
				name: 'user_verify_email',
				endpoint: config.apiGateway.URL + '/user_verify_email',
				region: config.apiGateway.REGION,
			},	
			{
				name: 'user_reset_pwd',
				endpoint: config.apiGateway.URL + '/user_reset_pwd',
				region: config.apiGateway.REGION,
			},	
			{
				name: 'user_update',
				endpoint: config.apiGateway.URL + '/user_update',
				region: config.apiGateway.REGION,
			},
			{
				name: 'task_completed',
				endpoint: config.apiGateway.URL + '/task_completed',
				region: config.apiGateway.REGION,
			},
			{
				name: 'task_completed_list',
				endpoint: config.apiGateway.URL + '/task_completed_list',
				region: config.apiGateway.REGION,
			},
			{
				name: 'task_comp',
				endpoint: config.apiGateway.URL + '/task_comp',
				region: config.apiGateway.REGION,
			},
			{
				name: 'task_costs_upd',
				endpoint: config.apiGateway.URL + '/task_costs_upd',
				region: config.apiGateway.REGION,
			},
			{
				name: 'task_by_month',
				endpoint: config.apiGateway.URL + '/task_by_month',
				region: config.apiGateway.REGION,
			},
			{
				name: 'task_annual_costs',
				endpoint: config.apiGateway.URL + '/task_annual_costs',
				region: config.apiGateway.REGION,
			},
			{
				name: 'assets',
				endpoint: config.apiGateway.URL + '/assets',
				region: config.apiGateway.REGION,
			},
			{
				name: 'assets_no_log',
				endpoint: config.apiGateway.URL + '/assets_no_log',
				region: config.apiGateway.REGION,
			},
			{
				name: 'assetList',
				endpoint: config.apiGateway.URL + '/assetList',
				region: config.apiGateway.REGION,
			},
			{
				name: 'asset_note',
				endpoint: config.apiGateway.URL + '/asset_note',
				region: config.apiGateway.REGION,
			},
			{
				name: 'asset_docs',
				endpoint: config.apiGateway.URL + '/asset_docs',
				region: config.apiGateway.REGION,
			},
			{
				name: 'del_asset_docs',
				endpoint: config.apiGateway.URL + '/del_asset_docs',
				region: config.apiGateway.REGION,
			},
			{
				name: 'del_task_log',
				endpoint: config.apiGateway.URL + '/del_task_log',
				region: config.apiGateway.REGION,
			},
			{
				name: 'wip',
				endpoint: config.apiGateway.URL + '/wip',
				region: config.apiGateway.REGION,
			},
			{
				name: 'move_completed_to_wip',
				endpoint: config.apiGateway.URL + '/move_completed_to_wip',
				region: config.apiGateway.REGION,
			},
			{
				name: 'complete_task',
				endpoint: config.apiGateway.URL + '/complete_task',
				region: config.apiGateway.REGION,
			},
			{
				name: 'costs',
				endpoint: config.apiGateway.URL + '/costs',
				region: config.apiGateway.REGION,				
			},
			{
				name: 'inventory',
				endpoint: config.apiGateway.URL + '/inventory',
				region: config.apiGateway.REGION,
			},
			{
				name: 'img',
				endpoint: config.apiGateway.URL + '/img',
				region: config.apiGateway.REGION,
			},
			{
				name: 'settings',
				endpoint: config.apiGateway.URL + '/settings',
				region: config.apiGateway.REGION,
			},
			{
				name: 'board',
				endpoint: config.apiGateway.URL + '/board',
				region: config.apiGateway.REGION,				
			},	
			{
				name: 'asset_locator',
				endpoint: config.apiGateway.URL + '/asset_locator',
				region: config.apiGateway.REGION,				
			},		
			{
				name: 'user_access',
				endpoint: config.apiLog.URL + '/user_access/',
				region: config.apiLog.REGION,
			},
			{
				name: 'user_update_enable',
				endpoint: config.apiLog.URL + '/user_update/',
				region: config.apiLog.REGION,
			},
			{
				name: 'user_update_access',
				endpoint: config.apiLog.URL + '/user_app_access_get',
				region: config.apiLog.REGION,
			},    
			{
				name: 'user_access_list',
				endpoint: config.apiLog.URL + '/user_access_list',
				region: config.apiLog.REGION,
			},
			{
				name: 'user_full_list',
				endpoint: config.apiLog.URL + '/user_full_list',
				region: config.apiLog.REGION,
			},
		]
	}
});

ReactDOM.render(
	<Provider store={store}>
		<Router>
			<I18nProvider>
				<DisplayModeProvider>
					<App />
				</DisplayModeProvider>
			</I18nProvider>
		</Router>
	</Provider>,
	document.getElementById('root')	

);
unregister();
